//admin sign up
import { Component, OnInit, ViewChild } from "@angular/core";
import { TuiInputModule, TuiInputPasswordModule } from "@taiga-ui/legacy";
import { Router, RouterLink } from "@angular/router";
import { getHttpErrorMessage } from "../../shared/utils/http-error.util";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiError,
} from "@taiga-ui/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { AdminService } from "../../core/services/admin.service";
import { catchError, from, of } from "rxjs";
import { TuiFieldErrorPipe, TuiTabs } from "@taiga-ui/kit";
import { AsyncPipe, CommonModule } from "@angular/common";
import { AuthService } from "../../core/services/auth.service";
import { NetworkStatusService } from "../../core/services/network-status-service.service";
import { RecaptchaModule, RecaptchaFormsModule, RecaptchaComponent } from 'ng-recaptcha';
import { environment } from "../../../environments/environment";
import { EnterSubmitDirective } from "../../core/directives/enter-submit.directive";

@Component({
  selector: "app-admin-login",
  imports: [
    EnterSubmitDirective,
    FormsModule,
    TuiAppearance,
    ReactiveFormsModule,
    TuiError,
    TuiFieldErrorPipe,
    AsyncPipe,
    TuiInputModule,
    TuiTabs,
    CommonModule,
    TuiButton,
    RouterLink,
    TuiInputPasswordModule,
    RecaptchaModule,
    RecaptchaFormsModule,
  ],
  templateUrl: "./admin-login.component.html",
  styleUrl: "./admin-login.component.scss",
})
export class AdminLoginComponent implements OnInit {
  @ViewChild(RecaptchaComponent) captchaElement!: RecaptchaComponent;

  loginForm: FormGroup;
  isLoading: boolean = false;
  isOnline: boolean = true;
  activeItemIndex = 0;
  authError: string = "";

  siteKey = environment.recaptchaSiteKey;
  captchaToken: string = '';
  captchaError: boolean = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private alerts: TuiAlertService,
    private networkStatusService: NetworkStatusService,
    private adminService: AdminService
  ) {
    const home = this.authService.getHomeUrlForCurrentUser();
    if (home) {
      this.router.navigateByUrl(home);
    }

    this.loginForm = this.fb.group({
      username: ["", [Validators.required, Validators.email]],
      password: ["", [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.networkStatusService.getNetworkStatus().subscribe(status => {
      this.isOnline = status;
      console.log('Network Status:', status ? 'Online' : 'Offline');
      if (!this.isOnline) {
        this.isLoading = true;
        this.alerts
          .open("Network error, please try again later.", {
            label: "Network error",
            appearance: "negative",
          })
          .subscribe();
      }
    });
  }

  executeCaptcha(): void {
    try {
      const captcha = this.captchaElement as RecaptchaComponent | undefined;
      if (!captcha || typeof captcha.execute !== "function") {
        this.alerts
          .open("Security check is still loading. Please wait a moment and try again.", {
            label: "Please wait",
            appearance: "warning",
          })
          .subscribe();
        return;
      }
      captcha.execute();
    } catch (err) {
      console.warn("Captcha execute failed:", err);
      this.alerts
        .open("Security check failed to start. Refresh the page and try again.", {
          label: "Captcha Error",
          appearance: "negative",
        })
        .subscribe();
    }
  }

  async onCaptchaResolved(captchaResponse: string | null): Promise<void> {
    if (!captchaResponse) {
      this.captchaError = true;
      this.alerts
        .open("Captcha verification failed. Please try again.", {
          label: "Captcha Error",
          appearance: "negative",
        })
        .subscribe();
      return;
    }

    this.captchaError = false;
    this.captchaToken = captchaResponse;

    this.submitForm();
  }

  submit(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.executeCaptcha();
  }

  private submitForm(): void {
    if (this.loginForm.valid) {
      this.isLoading = true;

      const loginData = {
        ...this.loginForm.value,
        captchaToken: this.captchaToken, // Attach captcha token to request
      };

      this.authService.postLogin(loginData)
        .pipe(
          catchError((error) => {
            this.isLoading = false;
            this.authError = getHttpErrorMessage(error);
            this.loginForm.setErrors({ invalid: true });
            this.alerts
              .open(getHttpErrorMessage(error), {
                label: "Authentication Error",
                appearance: "negative",
              })
              .subscribe();
            return of(null);
          })
        )
        .subscribe((res: any) => {
          if (res) {
            this.isLoading = false;
            const home = this.authService.getHomeUrlForCurrentUser() || "/admin/users";
            this.router.navigateByUrl(home);
          }
        });
    }
  }
}

